import { useEffect, useState } from 'react';

/**
 * Reactive viewport-width hook keyed to a single mobile breakpoint (default
 * 720px to match the page's SCSS breakpoints). Listens to matchMedia change
 * events, not resize, so it only fires when crossing the threshold —
 * minimal re-renders.
 */
export function useIsMobile(maxWidthPx: number = 720): boolean {
    const query = `(max-width: ${maxWidthPx}px)`;

    const getMatch = (): boolean => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(query).matches;
    };

    const [isMobile, setIsMobile] = useState<boolean>(getMatch);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

        // Safari < 14 only supports the deprecated addListener API
        if (mql.addEventListener) {
            mql.addEventListener('change', handler);
            return () => mql.removeEventListener('change', handler);
        }
        // @ts-ignore — legacy fallback
        mql.addListener(handler);
        // @ts-ignore — legacy fallback
        return () => mql.removeListener(handler);
    }, [query]);

    return isMobile;
}
